import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-step-guide-type-1';

/**
 * "How it works" numbered guide for explaining a SignNow process flow.
 * Two-column split: heading + intro text on the left (5/12), numbered step list on the right (7/12); stacks vertically on mobile.
 * Pairs an intro narrative with the steps, unlike type-2 which uses a full-width header above a 2-column step grid.
 *
 * @category SignNow
 * @slot heading - <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]>
 * @slot text - <me-text>
 * @example
 *   <sn-step-guide-type-1
 *     items='[{"title":"Identification of Parties","text":"Clearly state the names."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">How it works</me-heading>
 *     <me-text slot="text">Understanding the key elements.</me-text>
 *   </sn-step-guide-type-1>
 */
@Component({
  tag: 'sn-step-guide-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcHowItWorksNumbered {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Numbered step items.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Step title (shown in bold)
   * @item.property {string} text - Step description
   * @item.required title
   * @item.content title text
   * @minItems 4
   */
  @Prop({ mutable: true }) items?: { title?: string; text?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `items` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} text - Fallback body text (use slot="text" instead)
   * @property {array} items - Fallback step items (use items prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        text?: string;
        raw_html?: string;
        items?: { title?: string; text?: string }[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const items = Array.isArray(this.items)
      ? this.items
      : (Array.isArray(values?.items) ? values.items : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <me-row direction="column" directionLg="row">
          <me-column spanLg="5">
            <div class={`${COMPONENT_NAME}__left`}>
              <slot name="heading">
                {values?.title && <me-heading variant="4" tag="h4">{values.title}</me-heading>}
              </slot>
              <slot name="text">
                {values?.text && <me-text>{values.text}</me-text>}
              </slot>
            </div>
          </me-column>
          {items.length > 0 && (
            <me-column spanLg="7">
              <ul class={`${COMPONENT_NAME}__right`}>
                {items.map((item, index) => (
                  <li class={`${COMPONENT_NAME}__item`}>
                    <span class={`${COMPONENT_NAME}__number`} aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
                    <div class={`${COMPONENT_NAME}__item-content`}>
                      {item.title && <me-heading tag="h4" class={`${COMPONENT_NAME}__item-title`}>{item.title}:</me-heading>}
                      {item.text && <me-text tag="span" class={`${COMPONENT_NAME}__item-text`}> {item.text}</me-text>}
                    </div>
                  </li>
                ))}
              </ul>
            </me-column>
          )}
        </me-row>
      </me-section>
    );
  }
}
